// Demo scorecards — every first demo on a platform deal, scored on the AE demo rubric
// (2026-10-28). Sales management only: the sidebar entry and the API are both gated on
// MANAGER_USERS, so a rep never sees this view or its data.
//
// Two reads on one page. The AE table is the week-over-week view a manager scans first:
// one row per AE, the fourteen dimensions as tinted cells, team row on top. Below it, the
// demos themselves, newest first; a row expands into the full card — rationale and the
// verified quotes behind each score, what to keep, the one thing to change — and can hand
// the demo to the chat with its opportunity already primed, exactly as the Morning Brief
// does. Gong's own AI scorecard on the same call is shown where its export covered it, so
// the two scales can be compared rather than confused.

import { useEffect, useMemo, useState } from 'react'
import { fetchScorecard, fetchScorecards } from '../lib/api'
import type {
  ScorecardCard, ScorecardDetailResponse, ScorecardEvidence, ScorecardsResponse, ScorecardSummary,
} from '../lib/types'

const RANGES: Array<{ days: number; label: string }> = [
  { days: 7, label: 'Last 7 days' },
  { days: 14, label: '2 weeks' },
  { days: 28, label: '4 weeks' },
  { days: 56, label: '8 weeks' },
  { days: 91, label: 'Quarter' },
]

// Column headers for the fourteen dimensions, short enough for one table row.
const SHORT: Record<string, string> = {
  confidence: 'Conf', executive_presence: 'Exec', discovery: 'Disc', listening_curiosity: 'Listen',
  engagement: 'Engage', value_selling: 'Value', customization: 'Custom', competitive_positioning: 'Compet',
  storytelling: 'Story', objection_handling: 'Object', urgency: 'Urgency', closing: 'Close',
  next_steps: 'Next', decision_process: 'Decide',
}

function isoDaysAgo(days: number): string {
  const d = new Date()
  d.setDate(d.getDate() - days + 1)
  return d.toISOString().slice(0, 10)
}

function fmtDate(iso: string): string {
  const d = new Date(`${iso}T12:00:00`)
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
}

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd']
  const v = n % 100
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`
}

function fmtMin(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

// Tint by value: red for the bottom of the scale, neutral in the middle, green at the top.
// Same mapping for 1–5 dimension scores and 1–10 overalls, via the fraction of the scale.
function tint(v: number | null | undefined, max: number): string {
  if (v === null || v === undefined) return 'bg-marker-panel2 text-marker-muted'
  const f = (v - 1) / (max - 1)
  if (f >= 0.75) return 'bg-marker-good/25 text-marker-good'
  if (f >= 0.5) return 'bg-marker-good/10 text-marker-text'
  if (f >= 0.25) return 'bg-marker-warn/15 text-marker-warn'
  return 'bg-marker-bad/20 text-marker-bad'
}

function Cell({ v, max = 5, title }: { v: number | null | undefined; max?: number; title?: string }) {
  return (
    <td className="px-1 py-1 text-center">
      <span
        title={title}
        className={`inline-block min-w-[2rem] rounded px-1 py-0.5 font-mono text-xs tabular-nums ${tint(v, max)}`}
      >
        {v === null || v === undefined ? '–' : Number.isInteger(v) ? v : v.toFixed(1)}
      </span>
    </td>
  )
}

function Chip({ children, tone = 'muted', title }: { children: React.ReactNode; tone?: 'muted' | 'good' | 'warn' | 'bad' | 'accent'; title?: string }) {
  const cls = {
    muted: 'border-marker-border text-marker-muted',
    good: 'border-marker-good/40 text-marker-good',
    warn: 'border-marker-warn/40 text-marker-warn',
    bad: 'border-marker-bad/40 text-marker-bad',
    accent: 'border-marker-accent/40 text-marker-accent',
  }[tone]
  return (
    <span title={title} className={`px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider border whitespace-nowrap ${cls}`}>
      {children}
    </span>
  )
}

// ---- the AE table ---------------------------------------------------------------------

function AeTable({ data, onPick, picked }: { data: ScorecardsResponse; onPick: (ae: string) => void; picked: string }) {
  const rows = useMemo(
    () => [...data.by_ae].sort((a, b) => (b.overall_avg ?? -1) - (a.overall_avg ?? -1)),
    [data.by_ae],
  )
  const dims = data.dimensions
  const Row = ({ name, s, team }: { name: string; s: ScorecardSummary; team?: boolean }) => (
    <tr
      onClick={team ? undefined : () => onPick(picked === name ? '' : name)}
      className={
        (team ? 'bg-marker-panel2 font-semibold ' : 'cursor-pointer hover:bg-marker-panel2/60 ') +
        (picked === name ? 'ring-1 ring-marker-accent/60 ' : '') + 'border-b border-marker-border/60'
      }
    >
      <td className="px-2 py-1 text-sm whitespace-nowrap">{name}</td>
      <td className="px-2 py-1 text-xs text-marker-muted text-center tabular-nums">{s.scored}{s.demos !== s.scored ? ` / ${s.demos}` : ''}</td>
      <Cell v={s.overall_avg} max={10} title="Average overall (1–10, the model's holistic read of each demo)" />
      <Cell v={s.mean_avg} max={5} title="Average of the mean of the fourteen dimension scores (1–5)" />
      <td className="px-1 py-1 text-xs text-center tabular-nums text-marker-muted" title="Average percentile of these demos among all scored demos">
        {s.overall_pct_avg === null || s.overall_pct_avg === undefined ? '–' : `${Math.round(s.overall_pct_avg)}`}
      </td>
      {dims.map((d) => <Cell key={d.key} v={s.dims[d.key]} title={d.label} />)}
      <td className="px-2 py-1 text-xs text-center tabular-nums text-marker-muted">{s.next_step_dated_pct === null ? '–' : `${s.next_step_dated_pct}%`}</td>
    </tr>
  )
  return (
    <div className="overflow-x-auto rounded-lg border border-marker-border bg-marker-panel">
      <table className="min-w-full text-marker-text">
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-marker-muted border-b border-marker-border">
            <th className="px-2 py-2 text-left font-medium">AE</th>
            <th className="px-2 py-2 font-medium" title="Demos scored / first demos in the window">Demos</th>
            <th className="px-1 py-2 font-medium" title="Average overall, 1–10 (holistic, not the average of the dimensions)">Overall</th>
            <th className="px-1 py-2 font-medium" title="Average of the fourteen dimension scores, 1–5">Mean</th>
            <th className="px-1 py-2 font-medium" title="Average percentile among all scored demos (50 = typical)">Pctl</th>
            {dims.map((d) => <th key={d.key} className="px-1 py-2 font-medium" title={d.label}>{SHORT[d.key] || d.label}</th>)}
            <th className="px-2 py-2 font-medium" title="Share of demos that ended with a dated next step">Dated next</th>
          </tr>
        </thead>
        <tbody>
          {data.team && <Row name="Team" s={data.team} team />}
          {rows.map((r) => <Row key={r.ae} name={r.ae} s={r} />)}
        </tbody>
      </table>
    </div>
  )
}

// ---- one demo ---------------------------------------------------------------------------

function Quote({ e }: { e: ScorecardEvidence }) {
  const tone = e.speaker === 'PROSPECT' ? 'accent' : e.speaker === 'PS' ? 'warn' : 'muted'
  return (
    <div className="flex gap-2 items-start text-sm">
      <div className="flex flex-col items-end gap-0.5 pt-0.5 shrink-0">
        <Chip tone={tone}>{e.speaker}</Chip>
        <span className="text-[10px] font-mono text-marker-muted">{e.minute !== null && e.minute !== undefined ? `${e.minute} min` : ''}</span>
      </div>
      <blockquote className="border-l-2 border-marker-border pl-2 text-marker-text/90 italic">
        “{e.quote}”
        {e.verified === false && (
          <span className="not-italic ml-2 text-[10px] text-marker-warn" title={e.verify_note || ''}>not found verbatim</span>
        )}
      </blockquote>
    </div>
  )
}

function TalkBar({ card }: { card: ScorecardCard }) {
  const t = card.talk
  if (!t || t.source !== 'transcript') return null
  const parts = [
    ...t.curve.map((c) => ({ label: c.name, share: c.share ?? 0, cls: c.name === card.ae ? 'bg-marker-accent' : 'bg-marker-warn/70' })),
    { label: 'Practice', share: t.prospect_share ?? 0, cls: 'bg-marker-good/70' },
  ]
  return (
    <div>
      <div className="flex h-2 rounded overflow-hidden bg-marker-panel2">
        {parts.map((p) => <div key={p.label} className={p.cls} style={{ width: `${p.share}%` }} title={`${p.label} ${p.share}%`} />)}
      </div>
      <div className="mt-1 text-[11px] text-marker-muted">
        Talk share: {parts.map((p) => `${p.label} ${Math.round(p.share)}%`).join(' · ')}
        {typeof t.prospect_questions === 'number' ? ` · practice asked ${t.prospect_questions} questions` : ''}
        {typeof t.lobby_seconds === 'number' && t.lobby_seconds >= 60
          ? ` · the practice joined at ${fmtMin(t.lobby_seconds)}; nothing before that is scored or counted`
          : ''}
      </div>
    </div>
  )
}

function Detail({ d, onAsk }: { d: ScorecardDetailResponse; onAsk?: (c: ScorecardCard) => void }) {
  const { card, record, dimensions } = d
  if (!record) return <div className="text-sm text-marker-muted">No record stored.</div>
  return (
    <div className="space-y-4 pt-3">
      {card.situation && <p className="text-sm text-marker-text/90"><span className="text-marker-muted">The practice: </span>{card.situation}</p>}
      <TalkBar card={card} />
      <div className="grid md:grid-cols-2 gap-3">
        <div className="rounded border border-marker-good/30 bg-marker-good/5 p-3">
          <div className="text-[10px] uppercase tracking-wider text-marker-good mb-1">Keep doing</div>
          <p className="text-sm">{record.keep_doing?.text}</p>
          {record.keep_doing?.quote && <div className="mt-2"><Quote e={{ quote: record.keep_doing.quote, speaker: 'AE', minute: record.keep_doing.minute ?? null, verified: record.keep_doing.verified }} /></div>}
        </div>
        <div className="rounded border border-marker-warn/30 bg-marker-warn/5 p-3">
          <div className="text-[10px] uppercase tracking-wider text-marker-warn mb-1">Change one thing</div>
          <p className="text-sm">{record.change_one_thing?.text}</p>
          {record.change_one_thing?.quote && (
            <div className="mt-2">
              <Quote e={{ quote: record.change_one_thing.quote, speaker: (record.change_one_thing.speaker as ScorecardEvidence['speaker']) || 'AE', minute: record.change_one_thing.minute ?? null, verified: record.change_one_thing.verified }} />
            </div>
          )}
        </div>
      </div>
      <div className="text-sm">
        <span className="text-marker-muted">Overall {record.overall?.score}/10 — </span>{record.overall?.rationale}
        {card.gong_overall !== null && card.gong_overall !== undefined && (
          <span className="text-marker-muted"> (Gong's AI scorecard gave this call {card.gong_overall}/10.)</span>
        )}
      </div>
      <div className="text-[11px] text-marker-muted">
        The overall is the model's holistic read of the demo, not the average of the fourteen dimensions
        {card.mean_score !== null && card.mean_score !== undefined ? ` (that average is ${card.mean_score.toFixed(1)}/5)` : ''}.
        {card.overall_pct !== null && card.overall_pct !== undefined
          ? ` It sits at the ${ordinal(card.overall_pct)} percentile of every first demo scored on this rubric.`
          : ''}
      </div>
      <div className="text-sm flex flex-wrap gap-x-4 gap-y-1">
        <span><span className="text-marker-muted">Next step: </span>{record.next_step_agreed?.what || 'none recorded'}</span>
        <Chip tone={record.next_step_agreed?.dated ? 'good' : 'bad'}>{record.next_step_agreed?.dated ? 'dated' : 'not dated'}</Chip>
      </div>
      {record.ps_note && <p className="text-sm text-marker-muted"><span className="text-marker-text/80">PS: </span>{record.ps_note}</p>}
      <div className="divide-y divide-marker-border/60 rounded border border-marker-border">
        {dimensions.map((dim) => {
          const r = record.dimensions?.[dim.key]
          if (!r) return null
          const g = card.gong_scores?.[dim.key]
          return (
            <div key={dim.key} className="p-3 space-y-2">
              <div className="flex items-center gap-3">
                <span className={`inline-block min-w-[2.2rem] text-center rounded px-1 py-0.5 font-mono text-sm ${tint(r.na ? null : r.score, 5)}`}>
                  {r.na ? 'N/A' : r.score}
                </span>
                <span className="font-medium text-sm">{dim.label}</span>
                <span className="text-[11px] text-marker-muted hidden md:inline">{dim.what}</span>
                {g !== undefined && g !== null && <span className="ml-auto text-[10px] font-mono text-marker-muted" title="Gong's AI scorecard on this call">Gong {g}</span>}
              </div>
              <p className="text-sm text-marker-text/90">{r.rationale}</p>
              <div className="space-y-1.5">{(r.evidence || []).map((e, i) => <Quote key={i} e={e} />)}</div>
            </div>
          )
        })}
      </div>
      <div className="flex flex-wrap gap-2 items-center text-xs text-marker-muted">
        {card.gong_url && <a className="text-marker-accent hover:underline" href={card.gong_url} target="_blank" rel="noreferrer">Open the recording in Gong ↗</a>}
        {onAsk && card.opportunity_id && (
          <button onClick={() => onAsk(card)} className="ml-auto px-3 py-1 rounded border border-marker-accent/50 text-marker-accent hover:bg-marker-accent/10">
            Ask Marker about this demo
          </button>
        )}
        <span>
          {card.quotes_verified}/{card.quotes_total} quotes verified against the transcript · {card.model}
          {record.transcript_truncated ? ' · transcript truncated' : ''}
        </span>
      </div>
    </div>
  )
}

function DemoRow({ card, dims, open, onToggle, detail, onAsk }: {
  card: ScorecardCard
  dims: ScorecardsResponse['dimensions']
  open: boolean
  onToggle: () => void
  detail?: ScorecardDetailResponse | 'loading' | { error: string }
  onAsk?: (c: ScorecardCard) => void
}) {
  const scored = card.status === 'scored'
  return (
    <div className="rounded-lg border border-marker-border bg-marker-panel">
      <button onClick={onToggle} className="w-full text-left px-3 py-2 flex flex-wrap items-center gap-x-3 gap-y-1 hover:bg-marker-panel2/60 rounded-lg">
        <span className="text-xs font-mono text-marker-muted w-24 shrink-0">{fmtDate(card.demo_date)}</span>
        <span className="font-medium text-sm min-w-0 truncate max-w-xs">{card.account_name || card.call_title}</span>
        <span className="text-xs text-marker-muted">{card.ae}{card.ps ? ` · with ${card.ps}` : ''} · {card.duration_minutes} min</span>
        {scored ? (
          <>
            <span className={`ml-auto inline-block rounded px-2 py-0.5 font-mono text-sm ${tint(card.overall, 10)}`} title="Overall, 1–10 (holistic, not the average of the dimensions)">{card.overall}/10</span>
            {card.mean_score !== null && card.mean_score !== undefined && (
              <Chip title="Mean of the fourteen dimension scores (1–5)">mean {card.mean_score.toFixed(1)}</Chip>
            )}
            {card.overall_pct !== null && card.overall_pct !== undefined && (
              <Chip tone={card.overall_pct >= 75 ? 'good' : card.overall_pct <= 25 ? 'bad' : 'muted'} title="Percentile among every first demo scored on this rubric">
                {ordinal(card.overall_pct)} pctl
              </Chip>
            )}
            {card.gong_overall !== null && card.gong_overall !== undefined && <Chip title="Gong's AI scorecard on this call">Gong {card.gong_overall}</Chip>}
            <Chip tone={card.next_step_dated ? 'good' : 'bad'}>{card.next_step_dated ? 'dated next step' : 'no dated next step'}</Chip>
            <span className="hidden lg:flex gap-0.5">
              {dims.map((d) => (
                <span key={d.key} className={`inline-block w-6 text-center rounded font-mono text-[10px] ${tint(card.scores?.[d.key], 5)}`} title={d.label}>
                  {card.scores?.[d.key] ?? '–'}
                </span>
              ))}
            </span>
          </>
        ) : (
          <span className="ml-auto text-xs text-marker-warn">{card.status === 'error' ? 'scoring failed' : 'not scored'}: {card.reason}</span>
        )}
      </button>
      {open && scored && (
        <div className="px-3 pb-3 border-t border-marker-border/60">
          {!detail || detail === 'loading' ? (
            <div className="text-sm text-marker-muted pt-3">Loading the card…</div>
          ) : 'error' in detail ? (
            <div className="text-sm text-marker-bad pt-3">{detail.error}</div>
          ) : (
            <Detail d={detail} onAsk={onAsk} />
          )}
        </div>
      )}
    </div>
  )
}

// ---- the page ---------------------------------------------------------------------------

export function Scorecards({ onAskAboutDemo }: { onAskAboutDemo?: (card: ScorecardCard) => void }) {
  const [days, setDays] = useState(28)
  const [ae, setAe] = useState('')
  const [data, setData] = useState<ScorecardsResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [open, setOpen] = useState<string | null>(null)
  const [details, setDetails] = useState<Record<string, ScorecardDetailResponse | 'loading' | { error: string }>>({})

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    fetchScorecards({ start: isoDaysAgo(days) })
      .then((r) => { if (!cancelled) setData(r) })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [days])

  function toggle(callId: string) {
    const next = open === callId ? null : callId
    setOpen(next)
    if (next && !details[next]) {
      setDetails((d) => ({ ...d, [next]: 'loading' }))
      fetchScorecard(next)
        .then((r) => setDetails((d) => ({ ...d, [next]: r })))
        .catch((e) => setDetails((d) => ({ ...d, [next]: { error: e instanceof Error ? e.message : String(e) } })))
    }
  }

  const cards = useMemo(() => (data?.cards || []).filter((c) => !ae || c.ae === ae), [data, ae])

  return (
    <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-5 text-marker-text">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <h1 className="text-lg font-semibold">Demo scorecards</h1>
          <p className="text-xs text-marker-muted max-w-2xl">
            Every first demo on a platform deal, scored for the AE on the fourteen-dimension demo rubric (1–5 each, with the
            quotes behind each score checked against the transcript). The overall (1–10) is a separate, holistic read of the
            demo, not the average of the fourteen; the percentile places a demo among every first demo scored so far. Only the
            part of the recording the practice was on is scored. New demos appear the morning after Salesforce records the
            demo date. Sales management only.
          </p>
        </div>
        <div className="ml-auto flex flex-wrap gap-1">
          {RANGES.map((r) => (
            <button
              key={r.days}
              onClick={() => setDays(r.days)}
              className={`px-2 py-1 rounded text-xs border ${days === r.days ? 'border-marker-accent text-marker-accent bg-marker-accent/10' : 'border-marker-border text-marker-muted hover:text-marker-text'}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="text-sm text-marker-bad">{error}</div>}
      {loading && !data && <div className="text-sm text-marker-muted">Loading…</div>}

      {data && (
        <>
          <div className="text-xs text-marker-muted flex flex-wrap gap-x-4">
            <span>{data.window.start} → {data.window.end}</span>
            <span>{data.coverage.first_demos} first demos · {data.coverage.scored} scored{data.coverage.not_scored ? ` · ${data.coverage.not_scored} not scored` : ''}{data.coverage.error ? ` · ${data.coverage.error} failed` : ''}</span>
            {ae && <button className="text-marker-accent hover:underline" onClick={() => setAe('')}>Showing {ae} — show everyone</button>}
            {data.note && <span>{data.note}</span>}
          </div>

          {data.by_ae.length > 0 && (
            <section className="space-y-2">
              <h2 className="text-sm font-medium text-marker-muted">By AE <span className="font-normal">— averages over scored demos; click a row to filter the list</span></h2>
              <AeTable data={data} onPick={setAe} picked={ae} />
            </section>
          )}

          <section className="space-y-2">
            <h2 className="text-sm font-medium text-marker-muted">Demos <span className="font-normal">— newest first; click one for the full card</span></h2>
            {cards.length === 0 && <div className="text-sm text-marker-muted">No first demos in this window.</div>}
            {cards.map((c) => (
              <DemoRow
                key={c.call_id}
                card={c}
                dims={data.dimensions}
                open={open === c.call_id}
                onToggle={() => toggle(c.call_id)}
                detail={details[c.call_id]}
                onAsk={onAskAboutDemo}
              />
            ))}
          </section>
        </>
      )}
    </div>
  )
}
